export const SETTINGS_STORAGE_KEY = 'office-clash:settings';
export const SETTINGS_VERSION = 1 as const;

export const SETTINGS_ACTIONS = [
  'MOVE_LEFT', 'MOVE_RIGHT', 'JUMP', 'CROUCH', 'LIGHT_ATTACK',
  'HEAVY_ATTACK', 'SKILL_1', 'SKILL_2', 'SUPER', 'GUARD',
] as const;
export type SettingsAction = (typeof SETTINGS_ACTIONS)[number];
export type PlayerKeyBindings = Readonly<Record<SettingsAction, readonly string[]>>;

export interface PersistedSettingsV1 {
  readonly version: 1;
  readonly audio: Readonly<{ bgmVolume: number; seVolume: number; muted: boolean }>;
  readonly keyBindings: Readonly<{ p1: PlayerKeyBindings; p2: PlayerKeyBindings }>;
}
export type AppSettings = PersistedSettingsV1;
export type SettingsPatch = Readonly<{
  audio?: Partial<AppSettings['audio']>;
  keyBindings?: Partial<{ p1: PlayerKeyBindings; p2: PlayerKeyBindings }>;
}>;

export const RESERVED_CODES = Object.freeze(new Set(['Escape', 'Tab', ...Array.from({ length: 12 }, (_, index) => `F${index + 1}`)]));

const p1Defaults: PlayerKeyBindings = {
  MOVE_LEFT: ['KeyA'], MOVE_RIGHT: ['KeyD'], JUMP: ['KeyW'], CROUCH: ['KeyS'], LIGHT_ATTACK: ['KeyF'],
  HEAVY_ATTACK: ['KeyG'], SKILL_1: ['KeyH'], SKILL_2: ['KeyJ'], SUPER: ['Space'], GUARD: ['KeyK'],
};
const p2Defaults: PlayerKeyBindings = {
  MOVE_LEFT: ['ArrowLeft'], MOVE_RIGHT: ['ArrowRight'], JUMP: ['ArrowUp'], CROUCH: ['ArrowDown'],
  LIGHT_ATTACK: ['Numpad1', 'KeyU'], HEAVY_ATTACK: ['Numpad2', 'KeyI'], SKILL_1: ['Numpad4', 'KeyO'],
  SKILL_2: ['Numpad5', 'KeyP'], SUPER: ['Numpad0', 'KeyL'], GUARD: ['Numpad6', 'BracketLeft'],
};

const freezeBindings = (source: PlayerKeyBindings): PlayerKeyBindings => Object.freeze(Object.fromEntries(
  SETTINGS_ACTIONS.map((action) => [action, Object.freeze([...source[action]])]),
) as unknown as PlayerKeyBindings);

export const freezeSettings = (source: AppSettings): AppSettings => Object.freeze({
  version: 1,
  audio: Object.freeze({ ...source.audio }),
  keyBindings: Object.freeze({ p1: freezeBindings(source.keyBindings.p1), p2: freezeBindings(source.keyBindings.p2) }),
});

export const DEFAULT_SETTINGS: AppSettings = freezeSettings({
  version: 1, audio: { bgmVolume: 0.5, seVolume: 0.5, muted: false },
  keyBindings: { p1: p1Defaults, p2: p2Defaults },
});

export const validateKeyBindings = (p1: PlayerKeyBindings, p2: PlayerKeyBindings): void => {
  const globallyAssigned = new Map<string, string>();
  for (const [player, bindings] of [['p1', p1], ['p2', p2]] as const) {
    for (const action of SETTINGS_ACTIONS) {
      const codes: readonly string[] = bindings[action];
      if (!Array.isArray(codes) || codes.length === 0 || codes.some((code) => typeof code !== 'string' || code.length === 0)) {
        throw new RangeError(`${player}.${action} requires at least one KeyboardEvent.code.`);
      }
      for (const code of new Set<string>(codes)) {
        if (RESERVED_CODES.has(code)) throw new RangeError(`${code} is reserved.`);
        const owner = globallyAssigned.get(code);
        if (owner !== undefined && owner !== `${player}.${action}`) throw new RangeError(`${code} is already bound to ${owner}.`);
        globallyAssigned.set(code, `${player}.${action}`);
      }
    }
  }
};

export const validateSettings = (value: unknown): AppSettings => {
  if (typeof value !== 'object' || value === null) throw new TypeError('Settings must be an object.');
  const candidate = value as Partial<AppSettings>;
  if (candidate.version !== 1) throw new RangeError('Unsupported settings version.');
  const audio = candidate.audio;
  if (typeof audio !== 'object' || audio === null || typeof audio.bgmVolume !== 'number' || typeof audio.seVolume !== 'number'
    || typeof audio.muted !== 'boolean' || !Number.isFinite(audio.bgmVolume) || !Number.isFinite(audio.seVolume)
    || audio.bgmVolume < 0 || audio.bgmVolume > 1 || audio.seVolume < 0 || audio.seVolume > 1) {
    throw new RangeError('Invalid audio settings.');
  }
  const bindings = candidate.keyBindings;
  if (typeof bindings !== 'object' || bindings === null || typeof bindings.p1 !== 'object' || bindings.p1 === null
    || typeof bindings.p2 !== 'object' || bindings.p2 === null) throw new TypeError('Invalid key bindings.');
  validateKeyBindings(bindings.p1, bindings.p2);
  return freezeSettings(candidate as AppSettings);
};

export const mergeSettings = (current: AppSettings, patch: SettingsPatch): AppSettings => validateSettings({
  version: 1,
  audio: { ...current.audio, ...patch.audio },
  keyBindings: { ...current.keyBindings, ...patch.keyBindings },
});

export const remapBinding = (current: AppSettings, player: 'p1' | 'p2', action: SettingsAction, code: string): AppSettings => {
  const source = current.keyBindings[player];
  const replacement = Object.freeze(Object.fromEntries(SETTINGS_ACTIONS.map((key) => [
    key, Object.freeze(key === action ? [code] : [...source[key]]),
  ])) as unknown as PlayerKeyBindings);
  return mergeSettings(current, { keyBindings: { [player]: replacement } });
};
